/**
 * WHAT IS IN THIS BUILD AND NOT YET ON LIVE, GROUPED BY THE CARD IT BELONGS TO.
 *
 * WHY THIS EXISTS. The test copy wore a badge reading `2.7.1+41` and nothing
 * else. Vinnie, 27 August 2026: *"How can UAT be 2.7.1 + 32, I do not know what
 * is in it. I need to have a changelog in UAT so I can keep up with what we are
 * testing."* Right — the number says how far past live the build is and names
 * none of it, and the one place you would look, the badge itself, opened
 * FEATURES.md, which describes the version you are NOT testing.
 *
 * WHY THE CARD RATHER THAN THE COMMIT. A tester reads the board, so the list is
 * grouped the way the board is. A change that names no card is not hidden for
 * it — everything unclaimed goes under "Other changes", because a list that
 * quietly drops what it cannot classify is worse than one that admits to it.
 *
 * THE COUNT MUST MATCH THE BADGE. If the badge says +41 this lists 41, merge
 * commits and test-only changes included. A panel that showed 27 beside a badge
 * saying 41 would put the reader back where they started: unable to trust the
 * number.
 */

/**
 * A CARD REFERENCE, AND DELIBERATELY NOT A HEX COLOUR.
 *
 * `\b` after the digits is doing real work. A commit that mentions `#5b8def` —
 * and several do, because the areas palette is written in hex — would otherwise
 * be filed under card #5. Requiring a word boundary means `#5b8def` matches
 * nothing at all, which is the right answer: unclassifiable is a fine outcome,
 * filed under a card that has nothing to do with it is not.
 */
const REF = /#(\d+)\b/;

export interface UnreleasedGroup {
  /** The reference as it is written on the board, `#269`. */
  ref: string;
  /** The card's own title, or '' when this build cannot see that card. */
  title: string;
  changes: string[];
}

export interface Unreleased {
  groups: UnreleasedGroup[];
  other: string[];
  /** Everything listed, so a caller can check it against the badge. */
  listed: number;
}

/**
 * Group commit subjects, newest first, by the first card each one names.
 *
 * FIRST NAMED, NOT EVERY NAMED. A change mentioning two cards is listed once,
 * under the first. Listing it twice would read as two changes and make the
 * total disagree with the badge, which is the one thing this must not do.
 */
export function groupUnreleased(subjects: string[], titleOf: (ref: string) => string): Unreleased {
  const order: string[] = [];
  const byRef = new Map<string, string[]>();
  const other: string[] = [];

  for (const subject of subjects) {
    const m = subject.match(REF);
    if (!m) {
      other.push(subject);
      continue;
    }
    const ref = `#${m[1]}`;
    if (!byRef.has(ref)) {
      byRef.set(ref, []);
      order.push(ref);
    }
    byRef.get(ref)!.push(subject);
  }

  return {
    groups: order.map((ref) => ({ ref, title: titleOf(ref), changes: byRef.get(ref)! })),
    other,
    listed: subjects.length,
  };
}

/**
 * How many changes this build is past live, read from the `2.7.1+41` stamp.
 *
 * Read rather than counted so the panel and the badge cannot drift: they are
 * the same number from the same place. 0 when there is no stamp, which is what
 * a live build has.
 */
export function changesSinceLive(sinceLive: string): number {
  const n = Number(sinceLive.split('+')[1]);
  return Number.isFinite(n) ? n : 0;
}
